import React, { useState, useEffect, useRef, useCallback } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { ScrollArea } from '@/components/ui/scroll-area';
import { LayoutDashboard, Users, Award, Calendar, ClipboardList,
  BarChart3, Menu, X, Settings, Shield, User, LogOut, Moon, Sun, Globe, Lock, MapPin, RefreshCw, ChevronUp
} from 'lucide-react';
import { useSeason } from '@/contexts/SeasonContext';
import { OnlineIndicator } from '@/components/OnlineIndicator';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import api from '@/lib/api';

// All nav items with required permissions (null = always visible, array = any match)
const ALL_NAV_ITEMS = [
  { to: '/dashboard', labelKey: 'dashboard', icon: LayoutDashboard, perm: null },
  { to: '/sponsors', labelKey: 'sponsors', icon: Users, perm: 'view_sponsors' },
  { to: '/rights', labelKey: 'rights', icon: Award, perm: 'view_rights' },
  { to: '/clubs', labelKey: 'clubs', icon: Shield, perm: ['view_clubs', 'create_clubs', 'edit_clubs', 'delete_clubs'] },
  { to: '/stadiums', labelKey: 'stadiums', icon: MapPin, perm: ['view_stadiums', 'create_stadiums', 'edit_stadiums', 'delete_stadiums'] },
  { to: '/matches', labelKey: 'matches', icon: Calendar, perm: ['view_matches', 'create_matches', 'edit_matches', 'delete_matches'] },
  { to: '/requests', labelKey: 'requests', icon: ClipboardList, perm: ['view_requests', 'create_requests', 'create_requests_on_behalf', 'review_requests'] },
  { to: '/reports', labelKey: 'reports', icon: BarChart3, perm: ['view_reports', 'view_usage_report', 'create_reports'] },
  { to: '/settings', labelKey: 'settings', icon: Settings, perm: ['manage_settings', 'create_users', 'edit_users', 'delete_users', 'manage_roles', 'view_audit_log', 'manage_sessions', 'toggle_maintenance'] },
];

const NAV_GROUPS = [
  { title: 'Overview', paths: ['/dashboard'] },
  { title: 'Catalog', paths: ['/sponsors', '/rights', '/clubs', '/stadiums', '/matches'] },
  { title: 'Operations', paths: ['/requests', '/reports'] },
  { title: 'System', paths: ['/settings'] },
];

export default function Sidebar() {
  const { t, i18n } = useTranslation();
  const { user, logout, hasPerm } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { seasonOpen } = useSeason();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const isRTL = i18n.language === 'ar';
  const touchStartRef = useRef(null);

  const isSponsorRole = user?.role === 'sponsor' || user?.role === 'spo_viewer';
  const seasonClosed = !seasonOpen && isSponsorRole;
  const [submittedCount, setSubmittedCount] = useState(0);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [recentUsers, setRecentUsers] = useState([]);

  const lastSeenAgo = (dateStr) => {
    if (!dateStr) return '';
    const mins = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  useEffect(() => {
    const fetchCount = async () => {
      try {
        const { data } = await api.get('/api/requests/submitted-count');
        setSubmittedCount(data.count || 0);
      } catch { /* ignore */ }
    };
    const fetchOnline = async () => {
      try {
        const { data } = await api.get('/api/users/online');
        setOnlineUsers(data.users || []);
        setRecentUsers(data.recent || []);
      } catch { /* ignore */ }
    };
    fetchCount();
    if (hasPerm('view_online_users')) fetchOnline();
    const interval = setInterval(() => {
      fetchCount();
      if (hasPerm('view_online_users')) fetchOnline();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const perms = user?.permissions || [];
  const allItems = ALL_NAV_ITEMS.filter(item => {
    if (item.perm === null) return true;
    if (Array.isArray(item.perm)) return item.perm.some(p => perms.includes(p));
    return perms.includes(item.perm);
  });
  // When season is closed for sponsors, hide matches & requests
  const items = seasonClosed
    ? allItems.filter(item => !['/matches', '/requests'].includes(item.to))
    : allItems;

  useEffect(() => {
    document.documentElement.dir = i18n.language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  // Swipe gesture: left-to-right opens sidebar, right-to-left closes
  const handleTouchStart = useCallback((e) => {
    touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  }, []);

  const handleTouchEnd = useCallback((e) => {
    if (!touchStartRef.current) return;
    const dx = e.changedTouches[0].clientX - touchStartRef.current.x;
    const dy = Math.abs(e.changedTouches[0].clientY - touchStartRef.current.y);
    // Horizontal swipe must be > 60px and more horizontal than vertical
    if (Math.abs(dx) > 60 && dy < 100) {
      if (!isRTL && dx > 0 && touchStartRef.current.x < 40) setOpen(true);
      if (!isRTL && dx < 0 && open) setOpen(false);
      if (isRTL && dx < 0 && touchStartRef.current.x > window.innerWidth - 40) setOpen(true);
      if (isRTL && dx > 0 && open) setOpen(false);
    }
    touchStartRef.current = null;
  }, [open, isRTL]);

  useEffect(() => {
    const isLg = window.innerWidth >= 1024;
    if (isLg) return;
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });
    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchEnd]);

  const toggleLang = () => {
    const newLang = i18n.language === 'en' ? 'ar' : 'en';
    i18n.changeLanguage(newLang);
    localStorage.setItem('lang', newLang);
    document.documentElement.dir = newLang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = newLang;
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    setOpen(false);
  };

  return (
    <>
      {/* Mobile hamburger — only visible when sidebar is closed */}
      {!open && (
        <button
          data-testid="sidebar-mobile-toggle"
          className={`lg:hidden fixed top-4 ${isRTL ? 'right-4' : 'left-4'} z-[60] p-2 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 shadow-sm border border-gray-200 dark:border-gray-700`}
          onClick={() => setOpen(true)}
        >
          <Menu size={20} />
        </button>
      )}

      {/* Backdrop */}
      {open && <div className="lg:hidden fixed inset-0 bg-black/20 z-40" onClick={() => setOpen(false)} />}

      <aside
        data-testid="sidebar"
        className={`fixed top-0 ${isRTL ? 'right-0' : 'left-0'} h-full w-64 lg:w-full lg:static lg:h-full bg-[var(--page)] lg:bg-transparent z-50 flex flex-col
          transition-transform duration-300 ${open ? 'translate-x-0' : isRTL ? 'translate-x-full' : '-translate-x-full'} lg:translate-x-0`}
      >
        {/* Header: Logo + Close button */}
        <div className="px-[18px] pt-7 pb-6 shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <span className="text-[20px] font-semibold text-[var(--ink)] leading-none">INCEPTION</span>
              <span className="w-px h-5 bg-[var(--control)]" />
              <span className="text-[12px] font-semibold text-[var(--ink-2)] tracking-[.12em] leading-none">SPORT HUB</span>
            </div>
            <button
              data-testid="sidebar-close-btn"
              onClick={() => setOpen(false)}
              className="lg:hidden p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 px-[18px]">
          <nav className="space-y-5">
            {NAV_GROUPS.map(group => {
              const groupItems = items.filter(i => group.paths.includes(i.to));
              if (groupItems.length === 0) return null;
              return (
                <div key={group.title}>
                  <p className="text-[12px] text-[var(--ink-muted)] px-3 mb-1.5">{group.title}</p>
                  <div className="space-y-0.5">
                    {groupItems.map(item => (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        onClick={() => setOpen(false)}
                        className={({ isActive }) =>
                          `flex items-center gap-3 h-10 px-3 rounded-xl text-[14px] transition-colors duration-150
                          ${isActive
                            ? 'bg-[var(--brand)] text-[var(--on-brand)] font-medium'
                            : 'text-[var(--ink-muted)] hover:text-[var(--ink)] hover:bg-[var(--chip)]'}`
                        }
                      >
                        {({ isActive }) => (
                          <>
                            <item.icon size={20} strokeWidth={1.7} />
                            <span className="flex-1">{t(item.labelKey)}</span>
                            {item.to === '/requests' && submittedCount > 0 && (
                              <span
                                data-testid="requests-submitted-badge"
                                className={`min-w-[20px] h-[18px] flex items-center justify-center rounded-full text-[11px] font-medium px-1.5 leading-none tabular-nums
                                  ${isActive ? 'bg-[#1B1D21] text-white' : 'bg-[var(--alert)] text-white'}`}
                              >
                                {submittedCount}
                              </span>
                            )}
                          </>
                        )}
                      </NavLink>
                    ))}
                  </div>
                </div>
              );
            })}
          </nav>
          {seasonClosed && (
            <div data-testid="season-closed-sidebar-badge" className="mx-2 mt-4 px-3 py-2.5 rounded-xl bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800">
              <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
                <Lock size={13} />
                <span className="text-[11px] font-semibold">Season Closed</span>
              </div>
              <p className="text-[10px] text-amber-600 dark:text-amber-500 mt-1 leading-tight">Matches & requests are disabled by admin.</p>
            </div>
          )}
        </ScrollArea>

        {/* Bottom: Mobile-only quick actions + profile */}
        <div className="lg:hidden border-t border-gray-100 dark:border-gray-800 px-3 py-3 space-y-1">
          {/* Quick actions row */}
          <div className="flex items-center gap-1 px-2 pb-2">
            <button onClick={() => window.location.reload()} data-testid="refresh-page-mobile-btn" className="p-2 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
              <RefreshCw size={16} />
            </button>
            <button onClick={toggleTheme} className="p-2 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
              {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <button onClick={toggleLang} className="px-2.5 py-1.5 rounded-lg text-xs text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors font-medium flex items-center gap-1">
              <Globe size={14} /> {i18n.language === 'en' ? 'AR' : 'EN'}
            </button>
          </div>
          {/* Profile link */}
          <NavLink
            to="/profile"
            onClick={() => setOpen(false)}
            className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-medium text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all"
          >
            <div className="relative">
              <div className="h-7 w-7 rounded-full bg-gray-900 dark:bg-gray-200 flex items-center justify-center text-white dark:text-gray-900 text-[10px] font-semibold flex-shrink-0">
                {user?.name?.[0] || '?'}
              </div>
              <span className="absolute -bottom-0.5 -right-0.5"><OnlineIndicator isOnline={true} size="xs" /></span>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate leading-tight">{user?.name}</p>
              <p className="text-[10px] text-gray-400 dark:text-gray-500 truncate">{user?.role?.replace('_', ' ')}</p>
            </div>
          </NavLink>
          {/* Logout */}
          <button
            data-testid="mobile-logout-btn"
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-medium text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950 transition-all w-full"
          >
            <LogOut size={17} strokeWidth={1.8} />
            {t('logout') || 'Logout'}
          </button>
        </div>

        {/* Desktop: Active users button — internal roles only */}
        {hasPerm('view_online_users') && (
        <div data-testid="online-users-panel" className="hidden lg:block border-t border-gray-100 dark:border-gray-800 px-3 py-3">
          <Popover>
            <PopoverTrigger asChild>
              <button
                data-testid="active-users-btn"
                className="group w-full flex items-center gap-2 px-3 py-2 rounded-full text-[12px] font-medium text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse shrink-0" />
                {t('active', 'Active')}&nbsp;<span className="tabular-nums">({onlineUsers.length})</span>
                <ChevronUp size={14} className="ms-auto shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180" />
              </button>
            </PopoverTrigger>
            <PopoverContent
              data-testid="active-users-popover"
              side="top"
              align="start"
              sideOffset={8}
              className="w-[232px] p-2"
            >
              <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider px-2 pb-1.5">
                {t('online_now', 'Online now')} — {onlineUsers.length}
              </p>
              <div className="max-h-[320px] overflow-y-auto pe-1">
                {onlineUsers.map(u => (
                  <div key={u.id} data-testid={`online-user-${u.id}`} className="flex items-center gap-2 px-2 py-1.5">
                    <div className="relative shrink-0">
                      {u.avatar_url ? (
                        <img src={u.avatar_url} alt="" className="h-7 w-7 rounded-full object-cover" />
                      ) : (
                        <div className="h-7 w-7 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-[10px] font-bold text-gray-500 dark:text-gray-400">
                          {u.name?.[0] || '?'}
                        </div>
                      )}
                      <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full bg-green-500 border border-[var(--surface)]" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[12px] font-medium text-gray-700 dark:text-gray-300 truncate leading-tight">{u.name}</p>
                      <p className="text-[10px] text-gray-400 dark:text-gray-600 capitalize truncate">
                        {u.role?.replace('_', ' ')}{u.company_name ? ` · ${u.company_name}` : ''}
                      </p>
                    </div>
                  </div>
                ))}
                {onlineUsers.length === 0 && (
                  <p className="text-[11px] text-gray-400 dark:text-gray-600 px-2 py-2">No users online</p>
                )}
                {recentUsers.length > 0 && (
                  <>
                    <p className="text-[10px] font-semibold text-gray-300 dark:text-gray-600 uppercase tracking-wider px-2 pt-3 pb-1">Recently active</p>
                    {recentUsers.map(u => (
                      <div key={u.id} data-testid={`recent-user-${u.id}`} className="flex items-center gap-2 px-2 py-1.5 opacity-70">
                        <div className="relative shrink-0">
                          {u.avatar_url ? (
                            <img src={u.avatar_url} alt="" className="h-7 w-7 rounded-full object-cover grayscale" />
                          ) : (
                            <div className="h-7 w-7 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-[10px] font-bold text-gray-400 dark:text-gray-500">
                              {u.name?.[0] || '?'}
                            </div>
                          )}
                          <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full bg-gray-300 dark:bg-gray-600 border border-[var(--surface)]" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[12px] text-gray-500 dark:text-gray-400 truncate leading-tight">{u.name}</p>
                          <p className="text-[10px] text-gray-400 dark:text-gray-600 truncate">active {lastSeenAgo(u.last_seen)}</p>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            </PopoverContent>
          </Popover>
        </div>
        )}
      </aside>
    </>
  );
}
